/**
 * Dates for the mental-health follow-up, expressed the way the backend reads them.
 *
 * The backend stores followUpDueAt as an instant and then derives the reminder in Europe/London.
 * Formatting that instant with `.slice(0, 10)` reads its UTC date instead, which is a different
 * calendar day for anything between 23:00Z and midnight during BST - so a follow-up chosen late in
 * the evening would display, and on re-save become, a day earlier than the clinician chose.
 *
 * Everything here therefore goes through Europe/London, and every date sent to the backend is noon
 * UTC of the chosen day. Noon is deliberate: the UK is never more than an hour ahead of UTC and
 * never behind it, so noon UTC is the same calendar day in London in both BST and GMT. Midnight
 * would sit on the boundary and is one DST change away from being wrong.
 */
const UK_TIMEZONE = 'Europe/London';

/**
 * The Europe/London calendar day of an instant, as YYYY-MM-DD (the format <input type="date"> wants).
 *
 * @param {string|number|Date} value
 * @returns {string} '' when the value is missing or unparseable
 */
export function toUkDateString(value) {
    if (!value) {
        return '';
    }

    const date = new Date(value);

    if (isNaN(date.getTime())) {
        return '';
    }

    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: UK_TIMEZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(date);

    const partOfType = type => {
        const found = parts.find(part => part.type === type);

        return found ? found.value : '';
    };

    return `${partOfType('year')}-${partOfType('month')}-${partOfType('day')}`;
}

/**
 * Today's date in the UK, as YYYY-MM-DD.
 *
 * @returns {string}
 */
export function ukToday() {
    return toUkDateString(new Date());
}

/**
 * A UK calendar date a number of days from another one, as YYYY-MM-DD.
 *
 * @param {number} days
 * @param {string} [from] YYYY-MM-DD; defaults to today in the UK
 * @returns {string}
 */
export function addUkDays(days, from) {
    const base = new Date(`${from || ukToday()}T12:00:00.000Z`);

    base.setUTCDate(base.getUTCDate() + days);

    return toUkDateString(base);
}

/**
 * A UK calendar date as clinicians read it: DD-MM-YYYY.
 *
 * Display only. Everything that is stored, compared or handed to <input type="date"> stays in
 * YYYY-MM-DD - the date input accepts no other format, and the ordering comparisons that keep a
 * reminder out of the past rely on ISO strings sorting chronologically. DD-MM-YYYY does not.
 *
 * @param {string} ukDate YYYY-MM-DD
 * @returns {string} DD-MM-YYYY, or '' when there is no date
 */
export function displayUkDate(ukDate) {
    if (!ukDate) {
        return '';
    }

    const [year, month, day] = ukDate.split('-');

    return (year && month && day) ? `${day}-${month}-${year}` : '';
}

/**
 * An instant as the UK calendar date a clinician reads: DD-MM-YYYY.
 *
 * @param {string|number|Date} value
 * @returns {string} DD-MM-YYYY, or '' when the value is missing or unparseable
 */
export function toUkDisplayDate(value) {
    return displayUkDate(toUkDateString(value));
}

/**
 * A UK calendar date as the instant to send to the backend.
 *
 * @param {string} ukDate YYYY-MM-DD
 * @returns {string} an ISO instant, or '' when there is no date
 */
export function ukDateToInstant(ukDate) {
    return ukDate ? new Date(`${ukDate}T12:00:00.000Z`).toISOString() : '';
}

/**
 * An instant as the exact UK date and time, e.g. "Fri 9 Oct 2026, 14:05:32" - Europe/London, so BST
 * in summer and GMT in winter, whatever the browser's own time zone. Built from its parts so it reads
 * the same in every browser (they disagree on the commas and on "24:00" for midnight).
 *
 * @param {string|number|Date} value
 * @returns {string} '' when the value is missing or unparseable
 */
export function formatUkDateTime(value) {
    if (!value) {
        return '';
    }

    const date = new Date(value);

    if (isNaN(date.getTime())) {
        return '';
    }

    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: UK_TIMEZONE,
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
    }).formatToParts(date);

    const part = (type) => {
        const found = parts.find(item => item.type === type);

        return found ? found.value : '';
    };
    // Some engines still say "24" for midnight even with h23.
    const hour = part('hour') === '24' ? '00' : part('hour');

    return `${part('weekday')} ${part('day')} ${part('month')} ${part('year')}, ${hour}:${part('minute')}:${part('second')}`;
}

/**
 * An instant's UK date and time, as numbers-as-text, read with Europe/London.
 *
 * @param {Date} date  a valid Date
 * @returns {{year: string, month: string, day: string, hour: string, minute: string, second: string}}
 */
function ukPartsOf(date) {
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: UK_TIMEZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
    }).formatToParts(date);

    const part = (type) => {
        const found = parts.find(item => item.type === type);

        return found ? found.value : '';
    };

    return {
        year: part('year'),
        month: part('month'),
        day: part('day'),
        // Some engines still say "24" for midnight even with h23.
        hour: part('hour') === '24' ? '00' : part('hour'),
        minute: part('minute'),
        second: part('second'),
    };
}

/**
 * An instant as the UK time of day it is: "HH:mm", Europe/London - so a 10:00Z slot reads 11:00 in summer
 * and 10:00 in winter, whatever the browser's own zone and whatever the date is today.
 *
 * @param {string|number|Date} value
 * @returns {string} '' when the value is missing or unparseable
 */
export function formatUkTime(value) {
    if (!value && value !== 0) {
        return '';
    }

    const date = new Date(value);

    if (isNaN(date.getTime())) {
        return '';
    }

    const parts = ukPartsOf(date);

    return `${parts.hour}:${parts.minute}`;
}

/**
 * The UK's offset from UTC, in minutes, at an instant: 60 in BST, 0 in GMT.
 *
 * @param {string|number|Date} value
 * @returns {number}
 */
export function ukOffsetAt(value) {
    const date = new Date(value);
    const wholeSeconds = Math.floor(date.getTime() / 1000) * 1000;
    const parts = ukPartsOf(date);
    const wallClockAsUtc = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute, +parts.second);

    return Math.round((wallClockAsUtc - wholeSeconds) / 60000);
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * The UK's offset from UTC, in minutes, at a UK date and time of day - what that DAY is in, not what
 * today is: 26 Oct 2026 is GMT (0) even when asked in October BST. Sent as timezoneOffset: the server
 * works the offset out itself, and refuses a change of a session whose offset does not match the one it
 * works out (a page from before UK-time sessions); an older server still uses it.
 *
 * Read exactly as the server reads it: in the hour that happens twice when the clocks go back (01:00 to
 * 01:59 on the last Sunday of October) it is the FIRST one, BST (60). In the hour skipped when they go
 * forward there is no such time; the offset after the change is given (the server refuses the time).
 *
 * @param {string} ukDate  YYYY-MM-DD
 * @param {string} [time]  HH:mm, UK; noon when not given
 * @returns {number} today's offset when there is no usable date
 */
export function ukOffsetMinutes(ukDate, time) {
    const [year, month, day] = String(ukDate || '').split('-').map(Number);
    const [hour, minute] = String(time || '12:00').split(':').map(Number);

    if (!year || !month || !day || isNaN(hour) || isNaN(minute)) {
        return ukOffsetAt(new Date());
    }

    const wallClockAsUtc = Date.UTC(year, month - 1, day, hour, minute);
    // The offsets in force a day either side: both are the same, except around a clock change.
    const candidates = [ukOffsetAt(wallClockAsUtc - DAY_MS), ukOffsetAt(wallClockAsUtc + DAY_MS)];
    // Those under which the clock really reads this time on this day: one, two in the repeated hour, none
    // in the skipped hour.
    const readings = candidates.filter(offset => ukOffsetAt(wallClockAsUtc - offset * 60000) === offset);

    if (!readings.length) {
        return ukOffsetAt(wallClockAsUtc);
    }

    // Two: the earlier instant, which is the larger offset (BST).
    return Math.max.apply(null, readings);
}

/**
 * The instant a session's stored date and UTC time of day are (the API's startDate + startsAt, or
 * endDate + endsAt: the time is UTC, "HH:mm:ss").
 *
 * @param {string} date  YYYY-MM-DD
 * @param {string} time  HH:mm or HH:mm:ss, UTC
 * @returns {(Date|null)}
 */
export function sessionInstant(date, time) {
    if (!date || !time) {
        return null;
    }

    const clock = String(time).slice(0, 8);
    const instant = new Date(`${String(date).slice(0, 10)}T${clock.length === 5 ? `${clock}:00` : clock}Z`);

    return isNaN(instant.getTime()) ? null : instant;
}

/**
 * When a session runs, in UK time: from its first slot's start to its last slot's end when its slots are
 * there (`appointments` on the Appointments page, `slots` from the API) - the instants patients book -
 * else from its stored date and times.
 *
 * @param {object} session
 * @returns {{date: string, startsAt: string, endsAt: string}} YYYY-MM-DD and HH:mm, UK; '' when unknown
 */
export function sessionUkTimes(session) {
    const slots = (session && (session.appointments || session.slots)) || [];
    const instantsOf = key => slots
        .map(slot => new Date(slot && slot[key]).getTime())
        .filter(time => !isNaN(time));
    const starts = instantsOf('startsAt');
    const ends = instantsOf('endsAt');

    const start = starts.length
        ? new Date(Math.min.apply(null, starts))
        : sessionInstant(session && session.startDate, session && session.startsAt);
    const end = ends.length
        ? new Date(Math.max.apply(null, ends))
        : sessionInstant(session && session.endDate, session && session.endsAt);

    return {
        date: start ? toUkDateString(start) : '',
        startsAt: start ? formatUkTime(start) : '',
        endsAt: end ? formatUkTime(end) : '',
    };
}

/**
 * A 24-hour "HH:mm" as the session pickers show it: "hh:mm AM/PM".
 *
 * @param {string} time  HH:mm
 * @returns {string} '' when there is no time
 */
export function toTwelveHourTime(time) {
    const [hourText, minute] = String(time || '').split(':');
    const hour = parseInt(hourText, 10);

    if (isNaN(hour) || !minute) {
        return '';
    }

    const twelve = hour % 12 || 12;

    return `${twelve < 10 ? `0${twelve}` : twelve}:${minute.slice(0, 2)} ${hour < 12 ? 'AM' : 'PM'}`;
}
